import React, { Fragment, useEffect, useState, useCallback } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import Header from "../../components/Header/Header";
import Scrollbar from "../../components/scrollbar/scrollbar";
import Footer from "../../components/footer/Footer";
import SEO from "../../components/SEO/SEO";
import {
  getDemosByVertical,
  getStorefrontUrl,
  getDemoRoute,
  getDemoAvailability,
  ADMIN_PASSWORD,
  CUSTOMER_PASSWORD,
  DemoTenant,
  VerticalDemos,
} from "../../api/demoTenants";
import { rememberVerticalIntent } from "../../utils/verticalIntent";

const pageStyles = `
  /* Demo Page Shell */
  .demo-hero-section {
    position: relative;
    padding: 140px 0 60px;
    background: radial-gradient(circle at 50% 20%, rgba(0, 255, 151, 0.08) 0%, transparent 60%);
    overflow: hidden;
    text-align: center;
  }
  .demo-breadcrumb-list {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    list-style: none;
    padding: 0;
    margin: 0 0 16px;
    font-size: 13px;
    opacity: 0.75;
  }
  .demo-breadcrumb-list a {
    color: #fff;
    text-decoration: none;
  }
  .demo-breadcrumb-list a:hover {
    color: var(--color-primary);
  }
  .demo-badge-pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: rgba(0, 255, 151, 0.1);
    border: 1px solid rgba(0, 255, 151, 0.35);
    color: var(--color-primary);
    padding: 7px 18px;
    border-radius: 30px;
    font-size: 13px;
    font-weight: 600;
    margin-bottom: 20px;
    box-shadow: 0 4px 15px rgba(0, 255, 151, 0.15);
  }
  .demo-hero-title {
    font-size: 2.85rem;
    font-weight: 800;
    line-height: 1.2;
    margin-bottom: 18px;
    color: #ffffff;
    letter-spacing: -0.5px;
  }
  @media (max-width: 991px) {
    .demo-hero-title { font-size: 2.2rem; }
  }
  @media (max-width: 575px) {
    .demo-hero-title { font-size: 1.8rem; }
  }
  .demo-hero-desc {
    font-size: 1.15rem;
    line-height: 1.75;
    opacity: 0.85;
    max-width: 760px;
    margin: 0 auto 28px;
    color: #e2e8f0;
  }
  .demo-hero-trust-bullets {
    display: flex;
    justify-content: center;
    gap: 22px;
    flex-wrap: wrap;
    font-size: 13px;
    opacity: 0.85;
  }
  .demo-hero-trust-bullets span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  /* Command Center Card */
  .demo-command-card {
    background: rgba(11, 18, 38, 0.75);
    border: 1px solid rgba(0, 255, 151, 0.25);
    border-radius: 20px;
    padding: 36px;
    backdrop-filter: blur(16px);
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5), 0 0 30px rgba(0, 255, 151, 0.08);
    margin-bottom: 50px;
  }
  .demo-quick-start-box {
    background: linear-gradient(135deg, rgba(0, 255, 151, 0.12), rgba(0, 255, 151, 0.03));
    border: 1px solid rgba(0, 255, 151, 0.3);
    border-radius: 16px;
    padding: 28px;
    text-align: center;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  }
  .demo-quick-start-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 16px 36px;
    border-radius: 30px;
    font-size: 16px;
    font-weight: 700;
    text-decoration: none;
    border: none;
    cursor: pointer;
    background: var(--color-primary);
    color: #060e1a !important;
    transition: all 0.25s ease;
    box-shadow: 0 4px 20px rgba(0, 255, 151, 0.35);
    width: 100%;
    max-width: 380px;
  }
  .demo-quick-start-btn:hover {
    background: #00e084;
    transform: translateY(-2px);
    box-shadow: 0 8px 30px rgba(0, 255, 151, 0.5);
  }
  .demo-quick-start-btn:disabled {
    opacity: 0.65;
    cursor: wait;
    transform: none;
  }
  .demo-slots-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: rgba(0, 255, 151, 0.12);
    color: var(--color-primary);
    padding: 5px 14px;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 600;
    margin-top: 14px;
  }

  /* Credential Box */
  .demo-cred-panel {
    background: rgba(15, 23, 48, 0.7);
    border: 1px solid rgba(255, 255, 255, 0.09);
    border-radius: 14px;
    padding: 20px 22px;
    height: 100%;
  }
  .demo-cred-panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }
  .demo-cred-role-title {
    font-size: 15px;
    font-weight: 700;
    color: #fff;
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .demo-cred-field {
    background: rgba(0, 0, 0, 0.4);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 8px;
    padding: 10px 14px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 8px;
  }
  .demo-cred-label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.6px;
    opacity: 0.6;
    margin-bottom: 2px;
  }
  .demo-cred-val {
    font-family: monospace;
    font-size: 14px;
    font-weight: 600;
    color: var(--color-primary);
  }
  .demo-copy-btn {
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.15);
    color: #e2e8f0;
    border-radius: 6px;
    padding: 5px 10px;
    font-size: 11px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }
  .demo-copy-btn:hover {
    background: rgba(0, 255, 151, 0.15);
    border-color: var(--color-primary);
    color: var(--color-primary);
  }
  .demo-copy-btn.copied {
    background: rgba(0, 255, 151, 0.2);
    border-color: var(--color-primary);
    color: var(--color-primary);
  }

  /* Scenario Highlights */
  .demo-scenario-card {
    background: rgba(12, 20, 42, 0.6);
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 14px;
    padding: 22px;
    height: 100%;
    transition: all 0.25s ease;
  }
  .demo-scenario-card:hover {
    border-color: rgba(0, 255, 151, 0.35);
    transform: translateY(-2px);
  }
  .demo-scenario-icon {
    width: 44px;
    height: 44px;
    border-radius: 10px;
    background: rgba(0, 255, 151, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--color-primary);
    font-size: 18px;
    margin-bottom: 14px;
  }
  .demo-scenario-title {
    font-size: 15px;
    font-weight: 700;
    color: #fff;
    margin-bottom: 6px;
  }
  .demo-scenario-desc {
    font-size: 13px;
    line-height: 1.6;
    opacity: 0.75;
    margin: 0;
  }

  /* Instance Cards */
  .demo-instance-card {
    background: rgba(11, 18, 38, 0.7);
    border: 1px solid rgba(0, 255, 151, 0.16);
    border-radius: 16px;
    padding: 26px;
    transition: all 0.3s ease;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
  .demo-instance-card:hover {
    border-color: var(--color-primary);
    transform: translateY(-3px);
    box-shadow: 0 12px 35px rgba(0, 255, 151, 0.1);
  }
  .demo-instance-title {
    font-size: 1.15rem;
    font-weight: 700;
    color: #fff;
    margin-bottom: 4px;
  }
  .demo-instance-sub {
    font-size: 13px;
    opacity: 0.65;
    margin-bottom: 16px;
  }
  .demo-slot-indicator {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    padding: 4px 10px;
    border-radius: 12px;
    font-weight: 600;
  }
  .demo-slot-indicator.available {
    background: rgba(0, 255, 151, 0.12);
    color: #00ff97;
    border: 1px solid rgba(0, 255, 151, 0.25);
  }
  .demo-slot-indicator.busy {
    background: rgba(255, 100, 100, 0.12);
    color: #ff6464;
    border: 1px solid rgba(255, 100, 100, 0.25);
  }
  .demo-slot-indicator.loading {
    background: rgba(255, 255, 255, 0.05);
    color: rgba(255, 255, 255, 0.5);
  }
  .demo-slot-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    display: inline-block;
  }
  .demo-slot-dot.green { background: #00ff97; }
  .demo-slot-dot.red { background: #ff6464; }
  .demo-slot-dot.gray { background: rgba(255, 255, 255, 0.4); }
  @keyframes demo-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
  .demo-slot-dot.pulse { animation: demo-pulse 1.5s infinite; }

  /* Instance Actions */
  .demo-instance-actions {
    display: flex;
    gap: 10px;
    margin-top: 20px;
  }
  .demo-btn-launch {
    flex: 1;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 10px 16px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 700;
    text-decoration: none;
    background: var(--color-primary);
    color: #060e1a !important;
    transition: all 0.2s ease;
  }
  .demo-btn-launch:hover {
    background: #00e084;
    transform: translateY(-1px);
    box-shadow: 0 4px 15px rgba(0, 255, 151, 0.3);
  }
  .demo-btn-storefront {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 10px 14px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    text-decoration: none;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.15);
    color: #fff;
    transition: all 0.2s ease;
  }
  .demo-btn-storefront:hover {
    border-color: var(--color-primary);
    color: var(--color-primary);
  }

  /* Bottom Onboarding Card */
  .demo-bottom-bridge-card {
    background: linear-gradient(135deg, rgba(16, 27, 56, 0.8), rgba(9, 14, 28, 0.9));
    border: 1px solid rgba(0, 255, 151, 0.25);
    border-radius: 20px;
    padding: 48px 36px;
    text-align: center;
    margin-top: 60px;
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.5);
  }
`;

// Verticals whose marketing page isn't at /industry/:slug.
const BACK_LINK_PATHS: Record<string, string> = {
  pg: "/industry/pg-hostel-management-software",
};

const DemoPage: React.FC = () => {
  const { vertical } = useParams<{ vertical: string }>();
  const [verticalData, setVerticalData] = useState<VerticalDemos | null>(null);
  const [loading, setLoading] = useState(true);
  const [routing, setRouting] = useState(false);
  const [availability, setAvailability] = useState<Record<string, { free: number; total: number }>>({});
  const [totalFree, setTotalFree] = useState<number | null>(null);
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  // Track intent
  useEffect(() => {
    rememberVerticalIntent(vertical);
  }, [vertical]);

  // Fetch demo tenants dynamically from API
  useEffect(() => {
    if (!vertical) {
      setLoading(false);
      return;
    }
    setLoading(true);
    getDemosByVertical(vertical).then((data) => {
      setVerticalData(data);
      setLoading(false);
      if (data) {
        document.title = `Live Demo - ${data.verticalName} | AXOIX`;
      }
    });
  }, [vertical]);

  // Fetch real-time availability every 30 seconds
  const fetchAvailability = useCallback(async () => {
    if (!vertical) return;
    const data = await getDemoAvailability(vertical);
    if (data) {
      const avail: Record<string, { free: number; total: number }> = {};
      for (const t of data.tenants) {
        avail[t.subdomain] = { free: t.freeSlots, total: t.totalSlots };
      }
      setAvailability(avail);
      setTotalFree(data.totalFree);
    }
  }, [vertical]);

  useEffect(() => {
    fetchAvailability();
    const interval = setInterval(fetchAvailability, 30_000);
    return () => clearInterval(interval);
  }, [fetchAvailability]);

  // Copy to clipboard helper
  const handleCopy = (text: string, key: string) => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    }
  };

  // Quick Start — auto-route to least-loaded tenant
  const handleQuickStart = async () => {
    if (!vertical) return;
    setRouting(true);
    try {
      const best = await getDemoRoute(vertical);
      if (best?.dashboardUrl) {
        window.open(best.dashboardUrl, "_blank");
      } else {
        const first = verticalData?.demos[0];
        if (first) window.open(first.dashboardUrl, "_blank");
      }
    } catch {
      const first = verticalData?.demos[0];
      if (first) window.open(first.dashboardUrl, "_blank");
    } finally {
      setRouting(false);
    }
  };

  if (loading) {
    return (
      <Fragment>
        <div className="about-page inner-page">
          <div className="body_wrap o-clip">
            <Header />
            <main>
              <section className="demo-hero-section">
                <div className="container text-center">
                  <div className="demo-badge-pill">
                    <i className="fas fa-spinner fa-spin"></i> Initializing Sandbox
                  </div>
                  <h1 className="demo-hero-title">Connecting to Demo Cluster...</h1>
                  <p className="demo-hero-desc">
                    Allocating live sandbox instances and checking slot availability.
                  </p>
                </div>
              </section>
            </main>
            <Footer />
            <Scrollbar />
          </div>
        </div>
      </Fragment>
    );
  }

  if (!verticalData || verticalData.demos.length === 0) {
    return <Navigate to="/404" replace />;
  }

  const verticalName = verticalData.verticalName;
  const backPath = BACK_LINK_PATHS[vertical || ""] || `/industry/${vertical}`;

  return (
    <Fragment>
      <SEO
        title={`Live Demo - ${verticalName} Software Sandbox`}
        description={`Test-drive live ${verticalName} management dashboards with pre-loaded sample data, admin access, and customer storefront. No signup required.`}
        path={`/demo/${vertical}`}
      />
      <style>{pageStyles}</style>
      <div className="about-page inner-page">
        <div className="body_wrap o-clip">
          <Header />
          <main>
            {/* =========================================================================
                HERO SECTION — High-Impact Live Sandbox
               ========================================================================= */}
            <section className="demo-hero-section">
              <div className="container">
                <ul className="demo-breadcrumb-list">
                  <li><Link to="/">Home</Link></li>
                  <li>/</li>
                  <li><Link to={backPath}>{verticalName}</Link></li>
                  <li>/</li>
                  <li style={{ color: "var(--color-primary)" }}>Live Demo</li>
                </ul>

                <div className="demo-badge-pill">
                  <i className="fas fa-play-circle"></i>
                  <span>Live Interactive Sandbox • Instant Auto-Login</span>
                </div>

                <h1 className="demo-hero-title">
                  Explore {verticalName} Live Dashboards
                </h1>

                <p className="demo-hero-desc">
                  Test-drive fully operational {verticalName} instances pre-loaded with realistic Indian business data, automated workflows, and customer storefronts. Zero sign-up required.
                </p>

                <div className="demo-hero-trust-bullets">
                  <span><i className="fas fa-check text-primary me-1" style={{ color: "var(--color-primary)" }}></i> Instant 1-Click Launch</span>
                  <span><i className="fas fa-check text-primary me-1" style={{ color: "var(--color-primary)" }}></i> Pre-Loaded Realistic Data</span>
                  <span><i className="fas fa-check text-primary me-1" style={{ color: "var(--color-primary)" }}></i> Dual Admin & Customer Portals</span>
                  <span><i className="fas fa-check text-primary me-1" style={{ color: "var(--color-primary)" }}></i> Real-Time Slot Availability</span>
                </div>
              </div>
            </section>

            {/* =========================================================================
                COMMAND CENTER — 1-Click Launcher + Credentials Panel
               ========================================================================= */}
            <section className="pt-20 pb-50">
              <div className="container">
                <div className="demo-command-card">
                  <div className="row g-4 align-items-stretch">
                    {/* Quick Launchpad */}
                    <div className="col-lg-5">
                      <div className="demo-quick-start-box">
                        <div style={{ fontSize: "14px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--color-primary)", marginBottom: "8px" }}>
                          Fast Track
                        </div>
                        <h3 style={{ fontSize: "1.35rem", fontWeight: 700, color: "#fff", marginBottom: "12px" }}>
                          1-Click Direct Launch
                        </h3>
                        <p style={{ fontSize: "13px", opacity: 0.75, maxWidth: "340px", marginBottom: "22px", lineHeight: 1.6 }}>
                          Auto-routes you to the fastest, lowest-load sandbox instance with pre-authenticated permissions.
                        </p>

                        <button
                          className="demo-quick-start-btn"
                          onClick={handleQuickStart}
                          disabled={routing}
                        >
                          {routing ? (
                            <>
                              <i className="fas fa-spinner fa-spin"></i>
                              Allocating Session...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-bolt"></i>
                              Quick Start — Try Now
                            </>
                          )}
                        </button>

                        <div className="demo-slots-badge">
                          <i className="fas fa-signal"></i>
                          <span>
                            {totalFree !== null
                              ? `${totalFree} free slots across ${verticalData.demos.length} instances`
                              : "Live clusters online"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Universal Credentials */}
                    <div className="col-lg-7">
                      <div className="row g-3 h-100">
                        {/* Admin Role */}
                        <div className="col-sm-6">
                          <div className="demo-cred-panel">
                            <div className="demo-cred-panel-header">
                              <div className="demo-cred-role-title">
                                <i className="fas fa-user-shield" style={{ color: "var(--color-primary)" }}></i>
                                Admin Access
                              </div>
                              <span style={{ fontSize: "11px", color: "var(--color-primary)", fontWeight: 600 }}>Back-Office</span>
                            </div>
                            <p style={{ fontSize: "12px", opacity: 0.7, margin: "0 0 12px", lineHeight: 1.5 }}>
                              Full management control, operational matrices, billing runs, and analytics.
                            </p>

                            <div>
                              <div className="demo-cred-label">Universal Password</div>
                              <div className="demo-cred-field">
                                <span className="demo-cred-val">{ADMIN_PASSWORD}</span>
                                <button
                                  className={`demo-copy-btn ${copiedKey === "admin-pass" ? "copied" : ""}`}
                                  onClick={() => handleCopy(ADMIN_PASSWORD, "admin-pass")}
                                  type="button"
                                >
                                  <i className={`fas ${copiedKey === "admin-pass" ? "fa-check" : "fa-copy"}`}></i>
                                  {copiedKey === "admin-pass" ? "Copied" : "Copy"}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Customer Role */}
                        <div className="col-sm-6">
                          <div className="demo-cred-panel" style={{ borderColor: "rgba(139, 92, 246, 0.25)" }}>
                            <div className="demo-cred-panel-header">
                              <div className="demo-cred-role-title">
                                <i className="fas fa-user" style={{ color: "#a78bfa" }}></i>
                                Client Portal
                              </div>
                              <span style={{ fontSize: "11px", color: "#a78bfa", fontWeight: 600 }}>Self-Service</span>
                            </div>
                            <p style={{ fontSize: "12px", opacity: 0.7, margin: "0 0 12px", lineHeight: 1.5 }}>
                              Experience customer bookings, invoices, service orders, and support tickets.
                            </p>

                            <div>
                              <div className="demo-cred-label">Universal Password</div>
                              <div className="demo-cred-field">
                                <span className="demo-cred-val" style={{ color: "#a78bfa" }}>{CUSTOMER_PASSWORD}</span>
                                <button
                                  className={`demo-copy-btn ${copiedKey === "cust-pass" ? "copied" : ""}`}
                                  onClick={() => handleCopy(CUSTOMER_PASSWORD, "cust-pass")}
                                  type="button"
                                >
                                  <i className={`fas ${copiedKey === "cust-pass" ? "fa-check" : "fa-copy"}`}></i>
                                  {copiedKey === "cust-pass" ? "Copied" : "Copy"}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* =========================================================================
                    WHAT TO TEST — Guided Scenario Cards
                   ========================================================================= */}
                <div className="text-center mb-35">
                  <span className="sub-title mb-10" style={{ fontSize: "12px", textTransform: "uppercase", letterSpacing: "0.5px", color: "var(--color-primary)" }}>
                    Guided Sandbox Tour
                  </span>
                  <h2 style={{ fontSize: "1.75rem", fontWeight: 700, color: "#fff" }}>
                    4 Real-World Scenarios to Test-Drive
                  </h2>
                </div>

                <div className="row g-3 mb-60">
                  <div className="col-lg-3 col-sm-6">
                    <div className="demo-scenario-card">
                      <div className="demo-scenario-icon">
                        <i className="fas fa-sliders"></i>
                      </div>
                      <h4 className="demo-scenario-title">1. Operational Matrix</h4>
                      <p className="demo-scenario-desc">
                        Explore real-time units, tables, or beds. Drag-and-drop allocations and inspect status changes live.
                      </p>
                    </div>
                  </div>

                  <div className="col-lg-3 col-sm-6">
                    <div className="demo-scenario-card">
                      <div className="demo-scenario-icon">
                        <i className="fas fa-file-invoice-dollar"></i>
                      </div>
                      <h4 className="demo-scenario-title">2. Billing & UPI QR</h4>
                      <p className="demo-scenario-desc">
                        Test 1-click invoice generation with Indian GST breakdown and zero-fee dynamic UPI payment links.
                      </p>
                    </div>
                  </div>

                  <div className="col-lg-3 col-sm-6">
                    <div className="demo-scenario-card">
                      <div className="demo-scenario-icon">
                        <i className="fas fa-store"></i>
                      </div>
                      <h4 className="demo-scenario-title">3. Public Storefront</h4>
                      <p className="demo-scenario-desc">
                        Open the public customer website. Submit test booking inquiries or orders and see them land in the admin queue.
                      </p>
                    </div>
                  </div>

                  <div className="col-lg-3 col-sm-6">
                    <div className="demo-scenario-card">
                      <div className="demo-scenario-icon">
                        <i className="fas fa-chart-line"></i>
                      </div>
                      <h4 className="demo-scenario-title">4. Revenue Reports</h4>
                      <p className="demo-scenario-desc">
                        Review authentic revenue rolls, tax summaries, occupancy metrics, and operational performance KPIs.
                      </p>
                    </div>
                  </div>
                </div>

                {/* =========================================================================
                    AVAILABLE DEMO INSTANCES
                   ========================================================================= */}
                <div className="d-flex justify-content-between align-items-center mb-25 flex-wrap gap-2">
                  <div>
                    <h3 style={{ fontSize: "1.35rem", fontWeight: 700, color: "#fff", margin: 0 }}>
                      Available Demo Environments
                    </h3>
                    <p style={{ fontSize: "13px", opacity: 0.7, margin: "4px 0 0" }}>
                      Choose any instance below to launch directly with dedicated login credentials.
                    </p>
                  </div>
                  <div style={{ fontSize: "13px", opacity: 0.8 }}>
                    <i className="fas fa-server me-1" style={{ color: "var(--color-primary)" }}></i>
                    {verticalData.demos.length} {verticalData.demos.length === 1 ? "Active Cluster" : "Active Clusters"}
                  </div>
                </div>

                <div className="row g-4">
                  {verticalData.demos.map((demo: DemoTenant) => {
                    const avail = availability[demo.subdomain];
                    const isCopied = copiedKey === `email-${demo.slug}`;
                    return (
                      <div key={demo.slug} className="col-lg-6 col-xl-4">
                        <div className="demo-instance-card">
                          <div>
                            <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                              <h4 className="demo-instance-title">{demo.companyName}</h4>
                              {avail ? (
                                <div className={`demo-slot-indicator ${avail.free > 0 ? "available" : "busy"}`}>
                                  <span className={`demo-slot-dot ${avail.free > 0 ? "green" : "red pulse"}`}></span>
                                  {avail.free}/{avail.total} free
                                </div>
                              ) : (
                                <div className="demo-slot-indicator loading">
                                  <span className="demo-slot-dot gray pulse"></span>
                                  checking...
                                </div>
                              )}
                            </div>

                            <p className="demo-instance-sub">{demo.description}</p>

                            <div className="demo-cred-field mb-3" style={{ background: "rgba(0, 0, 0, 0.25)" }}>
                              <div>
                                <div className="demo-cred-label">Admin Email</div>
                                <div className="demo-cred-val" style={{ fontSize: "12px" }}>{demo.email}</div>
                              </div>
                              <button
                                className={`demo-copy-btn ${isCopied ? "copied" : ""}`}
                                onClick={() => handleCopy(demo.email, `email-${demo.slug}`)}
                                type="button"
                                title="Copy Email"
                              >
                                <i className={`fas ${isCopied ? "fa-check" : "fa-copy"}`}></i>
                              </button>
                            </div>
                          </div>

                          <div className="demo-instance-actions">
                            <a
                              href={demo.dashboardUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="demo-btn-launch"
                            >
                              <i className="fas fa-tachometer-alt"></i>
                              Launch Dashboard
                            </a>
                            {demo.storefrontUrl !== null && (
                              <a
                                href={getStorefrontUrl(demo.subdomain)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="demo-btn-storefront"
                                title="Open Public Storefront"
                              >
                                <i className="fas fa-store"></i>
                                Storefront
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* =========================================================================
                    BOTTOM ONBOARDING BRIDGE — Convert Sandbox Visitors into Signups
                   ========================================================================= */}
                <div className="demo-bottom-bridge-card">
                  <div className="demo-badge-pill mb-15">
                    <i className="fas fa-rocket"></i>
                    <span>Ready to Run Your Own Business?</span>
                  </div>
                  <h2 style={{ fontSize: "2rem", fontWeight: 800, color: "#fff", marginBottom: "14px" }}>
                    Start Your Free {verticalName} Account in 3 Minutes
                  </h2>
                  <p style={{ fontSize: "15px", opacity: 0.8, maxWidth: "620px", margin: "0 auto 28px", lineHeight: 1.7 }}>
                    Get your own isolated tenant database on `*.axoix.io` with custom domain support, GST billing, and team collaboration. Free forever tier available.
                  </p>

                  <div className="d-flex justify-content-center align-items-center gap-3 flex-wrap">
                    <Link
                      to={`/signup?vertical=${vertical}&from=demo`}
                      className="demo-quick-start-btn"
                      style={{ maxWidth: "260px" }}
                    >
                      <span className="text">Start Free Account →</span>
                    </Link>
                    <Link
                      to={backPath}
                      className="demo-btn-storefront"
                      style={{ padding: "15px 26px", borderRadius: "30px", fontWeight: 700 }}
                    >
                      <i className="fas fa-arrow-left me-2"></i>
                      Back to {verticalName}
                    </Link>
                    <Link
                      to="/pricing"
                      className="demo-btn-storefront"
                      style={{ padding: "15px 26px", borderRadius: "30px", fontWeight: 700 }}
                    >
                      View Pricing Plans
                    </Link>
                  </div>
                </div>

              </div>
            </section>
          </main>
          <Footer />
          <Scrollbar />
        </div>
      </div>
    </Fragment>
  );
};

export default DemoPage;
